// ConfigVAT.js
import * as React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { styled, alpha } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";
import swal from 'sweetalert';
import Checkbox from '@mui/material/Checkbox';

import Client from './Client';
import Config from './ClientConfig';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#273442",
    color: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '&:last-child td, &:last-child th': { border: 0 },
  '&.editing': { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
}));

class VATRow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client("none"),
      edit: props.edit || false,
      row: props.row || {},
      nameValid: !!(props.row && props.row.name),
      purchaseValid: true,
      sellValid: true,
      changed: !!props.edit,
      saving: false,
      codeValid: true,
    };
    this.toggleEdit = this.toggleEdit.bind(this);
    this.onChange = this.onChange.bind(this);
    this.save = this.save.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (this.props !== prevProps) {
      this.setState({
        row: this.props.row || {},
        edit: !!this.props.edit,
      });
    }
  }

  toggleEdit() { this.setState({ edit: !this.state.edit }); }

  onChange(e) {
    const id = e.target.id;
    const value = e.target.value;
    const row = { ...(this.state.row || {}) };

    if (id === 'name') {
      row.name = value;
      this.setState({ nameValid: !!(value && value.trim().length > 0) });
    } else if (id === 'purchase') {
      const num = value === '' ? '' : Number(value);
      row.purchase = value === '' ? '' : num;
      this.setState({ purchaseValid: value === '' || (!Number.isNaN(num) && num >= 0 && num <= 100) });
    } else if (id === 'sell') {
      const num = value === '' ? '' : Number(value);
      row.sell = value === '' ? '' : num;
      this.setState({ sellValid: value === '' || (!Number.isNaN(num) && num >= 0 && num <= 100) });
    } else if (id === 'printer_vat_code') {
      const raw = value;
      if (raw === '') {
        row.printer_vat_code = '';
        this.setState({ codeValid: true }); // empty allowed in UI, but will make VAT invisible
      } else {
        const num = Number(raw);
        row.printer_vat_code = raw === '' ? '' : num;
        this.setState({ codeValid: !Number.isNaN(num) && num >= 0 && num <= 10 }); // adjust max if you want
      }
    } else if (id === 'isActive') {
      row.isActive = !!e.target.checked;
    }

    this.setState({ row, changed: true });
  }

  save() {
    const { nameValid, purchaseValid, sellValid, codeValid, changed, row } = this.state;
    if (!changed || !nameValid || !purchaseValid || !sellValid || !codeValid) {
      return swal("Error", "Please fix validation errors before saving.", "error");
    }

    const payload = {
      name: row.name || '',
      purchase: row.purchase === '' ? 0 : row.purchase,
      sell: row.sell === '' ? 0 : row.sell,
      printer_vat_code: (row.printer_vat_code === '' || row.printer_vat_code === undefined) ? null : row.printer_vat_code,
      isActive: row.isActive ?? true,
    };

    this.setState({ saving: true });

    let resp;
    if (row.iref) {
      resp = this.state.client.pushData(`${Config.API_URL_VAT}${row.iref}/edit/`, payload);
    } else {
      resp = this.state.client.pushData(Config.API_URL_VAT, payload);
    }

    this.setState({ saving: false });

    if (!resp) {
      return swal("Error", "No response from server", "error");
    }

    if (resp.status === "ERROR") {
      return swal("Error", resp.message || "Save failed", "error");
    }

    const looksLikeDrfError =
      !resp.iref && (
        resp.__all__ || 
        resp.non_field_errors ||
        resp.detail ||
        resp.sell || resp.purchase || resp.name || resp.printer_vat_code
      );

    if (looksLikeDrfError) {
      const lines = [];
      Object.entries(resp).forEach(([k, v]) => {
        const label = (k === "__all__" || k === "non_field_errors") ? "Error" : k;  // ✅ optional
        const arr = Array.isArray(v) ? v : [String(v)];
        arr.forEach(msg => lines.push(`${label}: ${msg}`));
      });
      return swal("Error", lines.join("\n"), "error");
    }

    const uRow = {
      iref: resp.iref || row.iref,
      name: resp.name ?? payload.name,
      purchase: resp.purchase ?? payload.purchase,
      sell: resp.sell ?? payload.sell,
      printer_vat_code: resp.printer_vat_code ?? payload.printer_vat_code,
      isActive: resp.isActive ?? payload.isActive,
    };

    this.setState({ row: uRow, edit: false, changed: false });

    swal("Success", `VAT saved (${uRow.iref || 'new'})`, "success", { buttons: false, timer: 1400 })
      .then(() => this.props.onSaved && this.props.onSaved());
  }

  render() {
    const r = this.state.row || {};
    const canSave =
      this.state.edit &&
      this.state.changed &&
      this.state.nameValid &&
      this.state.purchaseValid &&
      this.state.sellValid &&
      this.state.codeValid &&
      !this.state.saving;

    return (
      <StyledTableRow key={r.iref || 0} className={this.state.edit ? 'editing' : ''}>
        <StyledTableCell align="left" width={140}>{r.iref || ""}</StyledTableCell>

        <StyledTableCell align="left" width={110}>
          {this.state.edit ? (
            <TextField
              id="printer_vat_code"
              label="Code"
              variant="filled"
              size="small"
              type="number"
              value={r.printer_vat_code ?? ''}
              onChange={this.onChange}
              onFocus={e => e.target.select()}
              inputProps={{ step: "1", min: "0", max: "10" }}
              error={!this.state.codeValid}
              sx={{
                minWidth: 90,
                '& .MuiFilledInput-root': { bgcolor: 'background.default' },
                '& .MuiFilledInput-root.Mui-focused': { bgcolor: 'background.default' },
              }}
              autoFocus={!!this.props.autoFocusKey}
            />
          ) : (r.printer_vat_code ?? '')}
        </StyledTableCell>

        <StyledTableCell>
          {this.state.edit ?
            <TextField
              id="name"
              label="Name"
              variant="filled"
              size="small"
              value={r.name || ''}
              onChange={this.onChange}
              onFocus={e => e.target.select()}
              inputProps={{ maxLength: "64" }}
              error={!this.state.nameValid}
              required
              sx={{
                minWidth: 240,
                '& .MuiFilledInput-root': { bgcolor: 'background.default' },
                '& .MuiFilledInput-root.Mui-focused': { bgcolor: 'background.default' },
              }}
            />
            : (r.name || '')
          }
        </StyledTableCell>

        <StyledTableCell align="left" width={160}>
          {this.state.edit ?
            <TextField
              id="purchase"
              label="Purchase %"
              variant="filled"
              size="small"
              type="number"
              value={r.purchase ?? ''}
              onChange={this.onChange}
              onFocus={e => e.target.select()}
              inputProps={{ step: "0.01", min: "0", max: "100" }}
              error={!this.state.purchaseValid}
              sx={{
                '& .MuiFilledInput-root': { bgcolor: 'background.default' },
                '& .MuiFilledInput-root.Mui-focused': { bgcolor: 'background.default' },
              }}
            />
            : (r.purchase ?? '')
          }
        </StyledTableCell>

        <StyledTableCell align="left" width={160}>
          {this.state.edit ?
            <TextField
              id="sell"
              label="Sell %"
              variant="filled"
              size="small"
              type="number"
              value={r.sell ?? ''}
              onChange={this.onChange}
              onFocus={e => e.target.select()}
              inputProps={{ step: "0.01", min: "0", max: "100" }}
              error={!this.state.sellValid}
              sx={{
                '& .MuiFilledInput-root': { bgcolor: 'background.default' },
                '& .MuiFilledInput-root.Mui-focused': { bgcolor: 'background.default' },
              }}
            />
            : (r.sell ?? '')
          }
        </StyledTableCell>

        <StyledTableCell align="left" width={90}>
          {this.state.edit ? (
            <Checkbox
              id="isActive"
              checked={!!(r.isActive ?? true)}
              onChange={this.onChange}
            />
          ) : ((r.isActive ?? true) ? 'Yes' : 'No')}
        </StyledTableCell>

        <StyledTableCell align="right" width={120}>
          <IconButton onClick={this.toggleEdit} title="Edit">
            <EditIcon sx={{ color: this.state.edit ? 'error.main' : 'text.disabled' }} />
          </IconButton>
          {canSave &&
            <IconButton onClick={this.save} title="Save" disabled={!canSave}>
              <SaveIcon sx={{ color: 'primary.main' }} />
            </IconButton>
          }
        </StyledTableCell>
      </StyledTableRow>
    );
  }
}

export default class VATPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client("none"),
      rows: [],
      addRow: false,
      queryData: {},

      // Bulk VAT update modal
      bulkOpen: false,
      bulkFrom: '',
      bulkTo: '',
    };
    this.toggleAdd = this.toggleAdd.bind(this);
    this.refresh = this.refresh.bind(this);
    this.openBulk = this.openBulk.bind(this);
    this.closeBulk = this.closeBulk.bind(this);
    this.runBulk = this.runBulk.bind(this);
    this.tableRef = React.createRef();
  }

  componentDidMount() { this.refresh(); }

  refresh() {
    const queryData = { orderBy: "name" };
    // const queryData = { orderBy: "printer_vat_code" };
    this.setState({ queryData });
    const data = this.state.client.getData(Config.API_URL_VAT, queryData);
    // VATViewSet.list returns: { "vat": [...] }
    const rows = data && Array.isArray(data.vat) ? data.vat : (Array.isArray(data) ? data : []);
    this.setState({ rows });
  }

  toggleAdd() {
    const addButton = this.state.addRow;
    this.setState({ addRow: !addButton }, () => {
      if (!addButton && this.tableRef.current) {
        // smooth scroll when opening the add row
        const el = this.tableRef.current;
        if (el.scrollTo) el.scrollTo({ top: 0, behavior: 'smooth' });
        else el.scrollTop = 0;
      }
    });
  }

  openBulk() { this.setState({ bulkOpen: true, bulkFrom: '', bulkTo: '' }); }
  closeBulk() { this.setState({ bulkOpen: false }); }

  runBulk() {
    const { bulkFrom, bulkTo } = this.state;
    if (!bulkFrom || !bulkTo) {
      return swal("Error", "Please choose both source and target VAT.", "error");
    }
    if (bulkFrom === bulkTo) {
      return swal("Error", "Source and target VAT must be different.", "error");
    }
    const resp = this.state.client.pushData(`${Config.API_URL_VAT}bulk-reassign/`, {
      from_iref: bulkFrom,
      to_iref: bulkTo,
    });
    if (!resp) return swal("Error", "No response from server.", "error");
    if (resp.status === "OK") {
      this.closeBulk();
      swal("Done", resp.message || `Updated ${resp.updated} products.`, "success");
      this.refresh();
    } else {
      swal("Error", resp.message || "Bulk update failed.", "error");
    }
  }

  render() {
    const rows = Array.isArray(this.state.rows) ? this.state.rows : [];

    return (
      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={{ maxHeight: '85vh', bgcolor: 'background.paper', borderRadius: 2, border: 1, borderColor: 'divider' }}
        ref={this.tableRef}
      >
        <Table stickyHeader sx={{ minWidth: 700 }} aria-label="VAT table">
          <TableHead>
            <TableRow>
              <StyledTableCell width={180}>
                <IconButton onClick={this.toggleAdd} title="Add VAT">
                  <Avatar alt="Add VAT" sx={{ width: 24, height: 24, color: '#273442' }}>
                    {this.state.addRow ? <FaMinusCircle /> : <FaPlusCircle />}
                  </Avatar>
                </IconButton>

                {/* Bulk VAT update opener */}
                <Tooltip title="Bulk update products VAT">
                  <Button
                    onClick={this.openBulk}
                    size="small"
                    startIcon={<SwapHorizIcon />}
                    sx={{
                      ml: 1,
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.6)',
                      bgcolor: 'rgba(255,255,255,0.08)',
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                    }}
                  >
                    Update VAT
                  </Button>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell>Code</StyledTableCell>
              <StyledTableCell>Name</StyledTableCell>
              <StyledTableCell align="left">Purchase %</StyledTableCell>
              <StyledTableCell align="left">Sell %</StyledTableCell>
              <StyledTableCell align="left">Active</StyledTableCell>
              <StyledTableCell align="right" width={120}>Action</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {this.state.addRow &&
              <VATRow
                key={"new"}
                edit={true}
                row={{
                  name: '',
                  purchase: 0,
                  sell: 0,
                  printer_vat_code: '',
                  isActive: true,
                }}
                onSaved={() => { this.toggleAdd(); this.refresh(); }}
                autoFocusKey
              />
            }
            {rows.map(r => (
              <VATRow key={r.iref} row={r} onSaved={this.refresh} />
            ))}
          </TableBody>
        </Table>

        {/* Bulk VAT modal */}
        <Dialog open={this.state.bulkOpen} onClose={this.closeBulk} maxWidth="sm" fullWidth>
          <DialogTitle>Bulk update VAT for products</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="bulk-from-label">From VAT</InputLabel>
                <Select
                  labelId="bulk-from-label"
                  label="From VAT"
                  value={this.state.bulkFrom}
                  onChange={(e) => this.setState({ bulkFrom: e.target.value })}
                >
                  {rows.map(v =>
                    <MenuItem key={v.iref} value={v.iref}>
                      {v.name} — code {v.printer_vat_code ?? '-'} — sell {v.sell ?? 0}%
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="bulk-to-label">To VAT</InputLabel>
                <Select
                  labelId="bulk-to-label"
                  label="To VAT"
                  value={this.state.bulkTo}
                  onChange={(e) => this.setState({ bulkTo: e.target.value })}
                >
                  {rows.map(v =>
                    <MenuItem key={v.iref} value={v.iref}>
                      {v.name} — sell {v.sell ?? 0}%
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mt: 2, color: 'text.secondary', fontSize: 13 }}>
              This will assign all products that currently use the “From VAT” to the selected “To VAT”.
              You can reverse this by running another bulk update.
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeBulk}>Cancel</Button>
            <Button variant="contained" onClick={this.runBulk}>Update products</Button>
          </DialogActions>
        </Dialog>
      </TableContainer>
    );
  }
}